import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// In-memory token (never persisted to localStorage)
let memoryToken = null;

// Configure axios defaults — cookies sent automatically
axios.defaults.withCredentials = true;

// Request interceptor — attaches in-memory token as Authorization header fallback
axios.interceptors.request.use((config) => {
    if (memoryToken && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${memoryToken}`;
    }
    return config;
});

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);
    const didInit = useRef(false);

    const storeToken = useCallback((newToken) => {
        memoryToken = newToken;
        setToken(newToken);
    }, []);

    const fetchUser = useCallback(async () => {
        // Cookie is sent automatically; backend reads it
        try {
            const response = await axios.get(`${API}/auth/me`);
            const { refreshed_token, ...userData } = response.data;
            setUser(userData);
            if (refreshed_token) {
                storeToken(refreshed_token);
            }
        } catch {
            // Cookie invalid or expired — clear state silently
            memoryToken = null;
            setToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [storeToken]);

    useEffect(() => {
        if (!didInit.current) {
            didInit.current = true;
            fetchUser();
        }
    }, [fetchUser]);

    const login = async (email, password) => {
        // Backend sets httpOnly cookie + returns token in body
        const response = await axios.post(`${API}/auth/login`, { email, password });
        const { access_token, user: userData } = response.data;

        storeToken(access_token);
        setUser(userData);

        return userData;
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`);
        } catch { /* ignore */ }
        memoryToken = null;
        setToken(null);
        setUser(null);
    };

    const isAdmin = () => user?.role === 'admin' || user?.role === 'global_admin';
    const isGlobalAdmin = () => user?.role === 'global_admin';
    const isEngineer = () => user?.role === 'engineer';

    const value = {
        user,
        token,
        loading,
        login,
        logout,
        isAdmin,
        isGlobalAdmin,
        isEngineer,
        isAuthenticated: !!user
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
